import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import { getQueryKey } from './useQueryIssue';

import type { Issue } from './types';

const MUTATION_KEY = 'mutate-issue-status';

import { Status } from './constants';

function useMutationStatus(
  args: { integrationId: number; issueId: number },
  options?: UseMutationOptions<void, Error, number, { issue: Issue }>
) {
  const { integrationId, issueId } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey(args);

  const mutationKey = MUTATION_KEY;
  const url = new URL(
    `/api/platform/cli/apps/${encodeURIComponent(
      integrationId
    )}/issues/${encodeURIComponent(issueId)}`,
    ZAPIER_APP_BASE_URL
  );

  const mutation = useMutation({
    mutationKey,
    async mutationFn(statusNext) {
      // Only allows to switch between open and resolved. It’s the only use
      // case today.
      if (statusNext !== Status.open && statusNext !== Status.resolved) {
        throw new Error(`${statusNext} is not a valid issue status`);
      }
      const body = JSON.stringify({ status: statusNext });
      fetchJson(url.href, { body, method: 'PATCH' });
    },
    onError: (error: any, _variables) => {
      notify.failure(error.message, mutationKey);
    },
    onSuccess: () => {
      notify.success('Successfully updated issue status.');
      queryClient.invalidateQueries(queryKey);
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationStatus;
